import React, { useState, useEffect, useCallback } from 'react';
import { ArrowRight, ChevronRight, Plus, Link2, FileAudio, FileVideo, Check } from 'lucide-react';
import UploadFlow from '../components/UploadFlow';
import AnonymousUploadFlow from '../components/AnonymousUploadFlow';
import ProductDemo from '../components/home/ProductDemo';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useGoogleOneTap } from '../hooks/useGoogleOneTap';
import { useReveal } from '../hooks/useReveal';
import { useTranslation } from 'react-i18next';
import { SEO, SITE_URL, breadcrumbJsonLd, organizationJsonLd, websiteJsonLd } from '../components/SEO';

const seoTitle = 'Audio & Video to Text Online Free | OmniScribe';
const seoDescription =
  'Convert audio, video, meetings, lectures, podcasts, and pasted links into accurate text. Supports 98+ languages, speaker labels, AI summaries, and exports.';
const supportedFormats = ['MP3', 'MP4', 'M4A', 'WAV', 'FLAC', 'OGG', 'OPUS', 'WEBM', 'MOV', 'MKV', 'WMA', 'AAC'];
const transcriptionUseCases = [
  'Speech to text',
  'Voice to text',
  'Audio to text',
  'Video to text',
  'Link to text',
  'MP3 to text',
  'MP4 to text',
  'Podcast transcription',
  'Meeting transcription',
  'Interview transcription',
  'Lecture transcription',
  'Subtitle generator',
];
const exportFormats = [
  { ext: 'TXT', tint: 'var(--tint-transcript)' },
  { ext: 'DOCX', tint: 'var(--tint-api)' },
  { ext: 'PDF', tint: 'var(--tint-export)' },
  { ext: 'SRT', tint: 'var(--tint-mindmap)' },
  { ext: 'VTT', tint: 'var(--tint-translate)' },
  { ext: 'CSV', tint: 'var(--tint-keypoints)' },
];
const languageSamples = [
  'English', '中文', 'Español', 'العربية', 'हिन्दी', 'Français', 'Deutsch', '日本語', '한국어', 'Português',
  'Русский', 'Italiano', 'Türkçe', 'Tiếng Việt', 'Bahasa Indonesia', 'Nederlands', 'Polski', 'ไทย', 'עברית', 'Svenska',
];

const SectionText: React.FC<{ num: string; tint: string; title: string; children: React.ReactNode }> = ({ num, tint, title, children }) => (
  <div className="reveal" style={{ ['--section-tint' as string]: tint }}>
    <div className="num">{num}</div>
    <h2 className="h-section mt-5 text-balance">{title}</h2>
    <div className="t-body mt-6 space-y-4 text-pretty">{children}</div>
  </div>
);

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, loginWithGoogleOneTap } = useAuth();
  const { t } = useTranslation();
  const [lastTrialTranscriptionId, setLastTrialTranscriptionId] = useState<string | null>(null);
  const revealRef = useReveal<HTMLDivElement>();

  const faqItems = [
    { question: t('home.faq.formats.question'), answer: t('home.faq.formats.answer') },
    { question: t('home.faq.accuracy.question'), answer: t('home.faq.accuracy.answer') },
    { question: t('home.faq.languages.question'), answer: t('home.faq.languages.answer') },
    { question: t('home.faq.speed.question'), answer: t('home.faq.speed.answer') },
    { question: t('home.faq.security.question'), answer: t('home.faq.security.answer') },
    { question: t('home.faq.editing.question'), answer: t('home.faq.editing.answer') },
    { question: t('home.faq.plans.question'), answer: t('home.faq.plans.answer') },
    { question: t('home.faq.apiAccess.question'), answer: t('home.faq.apiAccess.answer') }
  ];

  const howItWorksSteps = [
    { title: t('home.howItWorks.upload.title'), description: t('home.howItWorks.upload.description') },
    { title: t('home.howItWorks.transcribe.title'), description: t('home.howItWorks.transcribe.description') },
    { title: t('home.howItWorks.export.title'), description: t('home.howItWorks.export.description') }
  ];

  const homeJsonLd = [
    organizationJsonLd,
    websiteJsonLd,
    breadcrumbJsonLd([{ name: 'Home', path: '/' }]),
    {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'OmniScribe',
      url: SITE_URL,
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: seoDescription,
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
        description: 'Free monthly transcription minutes are available without a credit card.',
      },
      featureList: [
        'Audio to text transcription',
        'Video to text transcription',
        'Pasted link transcription',
        'AI summaries',
        'Mind map generation',
        'Speaker identification',
        'TXT, DOCX, PDF, SRT, VTT, and CSV exports',
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How to convert audio and video to text with OmniScribe',
      description: 'Upload a media file or paste a supported link, transcribe it with AI, then export or share the transcript.',
      step: howItWorksSteps.map((step, index) => ({
        '@type': 'HowToStep',
        position: index + 1,
        name: step.title,
        text: step.description,
      })),
    },
  ];

  const useCases = [
    { name: 'Creator workflow', role: 'Captions', text: t('home.testimonials.sarah') },
    { name: 'Team meetings', role: 'Meetings', text: t('home.testimonials.michael') },
    { name: 'Research interviews', role: 'Interviews', text: t('home.testimonials.emily') },
  ];

  useEffect(() => {
    try {
      setLastTrialTranscriptionId(localStorage.getItem('omniscribe:last_trial_transcription_id'));
    } catch {
      // ignore
    }
  }, []);

  const handleOneTapCredential = useCallback(
    async (credential: string) => {
      await loginWithGoogleOneTap(credential);
      navigate('/dashboard', { replace: true });
    },
    [loginWithGoogleOneTap, navigate]
  );

  const googleClientId = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined)?.trim();

  useGoogleOneTap({
    enabled: !isLoading && !isAuthenticated,
    clientId: googleClientId,
    onCredential: handleOneTapCredential,
  });

  return (
    <div ref={revealRef} className="flex flex-col">
      <SEO
        title={seoTitle}
        description={seoDescription}
        path="/"
        keywords={[
          'audio to text',
          'video to text',
          'convert audio to text online',
          'convert video to text online',
          'speech to text',
          'voice to text',
          'MP3 to text',
          'MP4 to text',
          'YouTube transcription',
          'AI transcription',
          'subtitle generator',
          'transcript summary',
        ]}
        jsonLd={homeJsonLd}
      />

      {/* Hero */}
      <section className="shell pb-10 pt-14 text-center sm:pt-20">
        <a
          href="#features"
          className="group mb-7 inline-flex max-w-full items-center gap-2 rounded-full border border-line py-1 pe-2.5 ps-1.5 text-[13px] font-medium text-ink-2 transition-colors hover:border-ink-3 hover:text-ink"
        >
          <span className="shrink-0 rounded-full bg-accent/10 px-2 py-0.5 text-[11px] font-semibold text-accent">{t('home.landing.badgeNew')}</span>
          <span className="truncate">{t('home.landing.badgeText')}</span>
          <ChevronRight className="h-3.5 w-3.5 text-ink-3 transition-transform group-hover:translate-x-0.5 rtl:rotate-180 rtl:group-hover:-translate-x-0.5" />
        </a>

        <h1 className="h-hero mx-auto max-w-[900px] text-balance">
          {t('home.landing.heroTitleLine1')}
          <br className="max-sm:hidden" /> {t('home.landing.heroTitleLine2')}
        </h1>
        <p className="t-lead mx-auto mt-6 max-w-[40rem] text-pretty">{t('home.heroSubtitle')}</p>

        <div className="mx-auto mt-10 max-w-2xl">
          {isLoading ? (
            <div className="flex h-[360px] items-center justify-center rounded-[24px] bg-raised shadow-window">
              <div className="h-7 w-7 animate-spin rounded-full border-2 border-accent border-t-transparent" />
            </div>
          ) : isAuthenticated ? (
            <UploadFlow isOpen={true} onClose={() => {}} embedded={true} />
          ) : (
            <AnonymousUploadFlow embedded={true} />
          )}

          {!isLoading && !isAuthenticated && lastTrialTranscriptionId && (
            <button
              type="button"
              onClick={() => navigate(`/trial/transcription/${lastTrialTranscriptionId}`)}
              className="group mt-4 flex w-full items-center justify-between gap-3 rounded-[18px] bg-card px-5 py-3.5 text-start transition-colors hover:bg-card-hi"
            >
              <span>
                <span className="block text-[14.5px] font-medium text-ink">{t('home.lastTrial')}</span>
                <span className="block text-[13px] text-ink-2">{t('home.trialExpiry')}</span>
              </span>
              <ChevronRight className="h-4 w-4 text-ink-3 transition-transform group-hover:translate-x-0.5 rtl:rotate-180" />
            </button>
          )}
        </div>

        <p className="t-small mt-6 flex flex-wrap justify-center gap-x-2">
          {[t('home.landing.metaFree'), t('home.landing.metaNoCard'), t('home.landing.metaLanguages')].map((item, i) => (
            <span key={item} className="whitespace-nowrap">
              {i > 0 && <span className="me-2 text-ink-3" aria-hidden="true">·</span>}
              {item}
            </span>
          ))}
        </p>
        <p className="mt-2 text-[12.5px] text-ink-3">
          By using OmniScribe, you agree to our{' '}
          <Link to="/terms" className="text-ink-2 underline decoration-ink-3 underline-offset-[3px] hover:text-ink">
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link to="/privacy" className="text-ink-2 underline decoration-ink-3 underline-offset-[3px] hover:text-ink">
            Privacy Policy
          </Link>
          .
        </p>
      </section>

      {/* Product window */}
      <section className="shell pb-8 pt-10">
        <div className="reveal">
          <ProductDemo />
        </div>
        <p className="t-small mx-auto mt-6 max-w-xl text-center text-ink-3">{t('home.landing.demoHint')}</p>
      </section>

      <div id="features" className="scroll-mt-20">
        {/* 01 Upload */}
        <section className="shell grid items-center gap-x-20 gap-y-12 py-24 sm:py-32 lg:grid-cols-[5fr_6fr]">
          <SectionText num="01" tint="var(--tint-transcript)" title={t('home.landing.s1Title')}>
            <p>{t('home.landing.s1Body')}</p>
          </SectionText>
          <div className="reveal panel p-3 sm:p-4">
            <div className="flex items-center gap-3 px-1 pb-3">
              <span className="text-[13px] font-medium text-ink-2">{t('nav.transcriptions')}</span>
              <span className="tnum ms-auto text-[13px] text-ink-3">3</span>
            </div>
            <div className="space-y-1 rounded-[18px] bg-raised p-1.5">
              {[
                { icon: FileVideo, name: 'customer-interview.mp4', meta: '1.2 GB · MP4', status: 'Converting in browser', pct: 38, tint: 'var(--tint-export)' },
                { icon: FileAudio, name: 'podcast-episode-42.mp3', meta: '86 MB · MP3', status: 'Transcribing', pct: 72, tint: 'var(--tint-transcript)' },
                { icon: Link2, name: 'youtube.com/watch?v=…', meta: 'Link import', status: 'Done', pct: 100, tint: 'var(--tint-mindmap)' },
              ].map((row) => (
                <div key={row.name} className="flex items-center gap-3 rounded-[14px] px-3 py-2.5">
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px]" style={{ background: `rgb(${row.tint} / 0.13)`, color: `rgb(${row.tint})` }}>
                    <row.icon className="h-[18px] w-[18px]" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[14.5px] font-semibold text-ink">{row.name}</div>
                    <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-card-hi">
                      <div className="h-full rounded-full" style={{ width: `${row.pct}%`, background: `rgb(${row.tint})` }} />
                    </div>
                  </div>
                  <div className="w-36 shrink-0 text-end max-sm:hidden">
                    <div className="text-[12.5px] font-medium" style={{ color: `rgb(${row.tint})` }}>
                      {row.pct === 100 ? <span className="inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" />{row.status}</span> : row.status}
                    </div>
                    <div className="tnum text-[12px] text-ink-3">{row.meta}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5 px-1 pt-4">
              {supportedFormats.map((format) => (
                <span key={format} className="rounded-full bg-raised px-2.5 py-1 text-[12px] font-semibold text-ink-2">{format}</span>
              ))}
            </div>
          </div>
        </section>

        {/* 02 Speakers */}
        <section className="shell grid items-center gap-x-20 gap-y-12 py-24 sm:py-32 lg:grid-cols-[6fr_5fr]">
          <div className="reveal panel p-3 sm:p-4 lg:order-none max-lg:order-2">
            <div className="space-y-1 rounded-[18px] bg-raised p-2">
              {[
                { who: 'Speaker 1', as: 'Interviewer', c: '#007aff', t: '04:12', text: 'What made you switch tools last year?' },
                { who: 'Speaker 2', as: 'Guest', c: '#af52de', t: '04:15', text: 'Honestly, the time we lost cleaning up transcripts by hand.' },
                { who: 'Speaker 1', as: 'Interviewer', c: '#007aff', t: '04:21', text: 'And how long does that take now?' },
              ].map((row, i) => (
                <div key={i} className={`flex gap-3 rounded-[14px] px-3 py-3 ${i === 1 ? 'bg-card' : ''}`}>
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-[12px] font-semibold text-white" style={{ background: row.c }}>
                    {row.as[0]}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[13.5px] font-semibold text-ink">{row.as}</span>
                      <s className="text-[12px] text-ink-3">{row.who}</s>
                      <span className="tnum ms-auto text-[12px] text-ink-3">{row.t}</span>
                    </div>
                    <p className="mt-0.5 text-[14.5px] leading-relaxed text-ink">
                      {i === 1 ? (
                        <>
                          Honestly, the time we lost cleaning up{' '}
                          <span className="rounded-[4px] bg-accent/15 px-0.5 text-ink ring-1 ring-accent/40">transcripts</span>
                          <span className="ms-px inline-block h-[1.1em] w-[2px] translate-y-[3px] animate-pulse bg-accent" /> by hand.
                        </>
                      ) : (
                        row.text
                      )}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <SectionText num="02" tint="var(--tint-summary)" title={t('home.landing.s2Title')}>
            <p>{t('home.landing.s2Body')}</p>
          </SectionText>
        </section>

        {/* 03 AI insights */}
        <section className="shell grid items-center gap-x-20 gap-y-12 py-24 sm:py-32 lg:grid-cols-[5fr_6fr]">
          <SectionText num="03" tint="var(--tint-mindmap)" title={t('home.landing.s3Title')}>
            <p>{t('home.landing.s3Body')}</p>
          </SectionText>
          <div className="reveal grid gap-3 sm:grid-cols-3">
            {[
              { k: 'transcription.tabs.summary', tint: 'var(--tint-summary)', lines: [92, 100, 78, 86, 54] },
              { k: 'transcription.tabs.mindmap', tint: 'var(--tint-mindmap)', lines: [] },
              { k: 'transcription.tabs.keypoints', tint: 'var(--tint-keypoints)', lines: [70, 88, 64] },
            ].map((card) => (
              <div key={card.k} className="panel flex aspect-[3/4] flex-col !rounded-[22px] p-5 max-sm:aspect-auto max-sm:min-h-[180px]">
                <span className="chip w-fit" style={{ ['--chip-tint' as string]: card.tint }}>{t(card.k)}</span>
                <div className="mt-5 flex-1">
                  {card.lines.length > 0 ? (
                    <div className="space-y-2.5">
                      {card.lines.map((w, i) => (
                        <div key={i} className="flex items-center gap-2">
                          {card.k.endsWith('keypoints') && (
                            <span className="figure text-[12px]" style={{ color: `rgb(${card.tint})` }}>{i + 1}</span>
                          )}
                          <div className="h-2 rounded-full bg-ink/[0.1]" style={{ width: `${w}%` }} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <svg viewBox="0 0 120 120" className="h-full w-full" aria-hidden="true">
                      {[[22, 30], [22, 90], [98, 22], [98, 60], [98, 98]].map(([x, y], i) => (
                        <path key={i} d={`M60 60 Q ${(60 + x) / 2} ${y} ${x} ${y}`} fill="none" style={{ stroke: `rgb(${card.tint})` }} strokeOpacity="0.5" strokeWidth="2" />
                      ))}
                      {[[22, 30], [22, 90], [98, 22], [98, 60], [98, 98]].map(([x, y], i) => (
                        <circle key={i} cx={x} cy={y} r="7" style={{ fill: `rgb(${card.tint})` }} fillOpacity="0.25" />
                      ))}
                      <circle cx="60" cy="60" r="13" style={{ fill: `rgb(${card.tint})` }} />
                    </svg>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 04 Translate */}
        <section className="shell grid items-center gap-x-20 gap-y-12 py-24 sm:py-32 lg:grid-cols-[6fr_5fr]">
          <div className="reveal panel overflow-hidden p-6 max-lg:order-2 sm:p-8">
            <div className="flex flex-wrap gap-2">
              {languageSamples.map((name, i) => (
                <span
                  key={name}
                  className={`rounded-full px-3.5 py-1.5 text-[14px] font-medium ${i === 1 ? 'bg-tint-translate text-white' : 'bg-raised text-ink'}`}
                >
                  {name}
                </span>
              ))}
              <span className="rounded-full px-3.5 py-1.5 text-[14px] font-semibold text-tint-translate">+78</span>
            </div>
          </div>
          <SectionText num="04" tint="var(--tint-translate)" title={t('home.landing.s4Title')}>
            <p>{t('home.landing.s4Body')}</p>
          </SectionText>
        </section>

        {/* 05 Export */}
        <section className="shell grid items-center gap-x-20 gap-y-12 py-24 sm:py-32 lg:grid-cols-[5fr_6fr]">
          <SectionText num="05" tint="var(--tint-export)" title={t('home.landing.s5Title')}>
            <p>{t('home.landing.s5Body')}</p>
          </SectionText>
          <div className="reveal grid grid-cols-3 gap-3">
            {exportFormats.map((f) => (
              <div key={f.ext} className="panel flex aspect-square flex-col items-center justify-center gap-3 !rounded-[22px]">
                <span
                  className="relative grid h-14 w-11 place-items-end rounded-[6px] pb-1.5 shadow-[0_6px_14px_-6px_rgba(0,0,0,0.3)]"
                  style={{ background: `rgb(${f.tint})` }}
                  aria-hidden="true"
                >
                  <span className="absolute end-0 top-0 h-3 w-3 rounded-bl-[4px] bg-white/40" />
                  <span className="w-full text-center text-[9px] font-bold tracking-wide text-white">{f.ext}</span>
                </span>
                <span className="text-[13.5px] font-semibold text-ink">.{f.ext.toLowerCase()}</span>
              </div>
            ))}
          </div>
        </section>

        {/* 06 API */}
        <section className="shell grid items-center gap-x-20 gap-y-12 py-24 sm:py-32 lg:grid-cols-[6fr_5fr]">
          <div className="reveal overflow-hidden rounded-[22px] bg-[#1d1d1f] text-start shadow-window max-lg:order-2 dark:bg-[#0c0c0e]">
            <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
              <i className="h-2.5 w-2.5 rounded-full bg-white/20" />
              <i className="h-2.5 w-2.5 rounded-full bg-white/20" />
              <i className="h-2.5 w-2.5 rounded-full bg-white/20" />
              <span className="ms-2 font-mono text-[12px] text-white/50">terminal</span>
            </div>
            <pre className="overflow-x-auto p-5 font-mono text-[12.5px] leading-[1.75] text-white/80" dir="ltr">
              <span className="text-white/40"># 1. Request an upload URL, PUT the file, confirm</span>{'\n'}
              <span className="text-[#7d7aff]">curl</span> -X POST $BASE_URL/api/v1/openapi/files/upload \{'\n'}
              {'  '}-H <span className="text-[#30d158]">"X-API-Key: $API_KEY"</span> \{'\n'}
              {'  '}-d <span className="text-[#ffb340]">'{'{'}"file_name":"meeting.m4a", …{'}'}'</span>{'\n\n'}
              <span className="text-white/40"># 2. Start the transcription</span>{'\n'}
              <span className="text-[#7d7aff]">curl</span> -X POST $BASE_URL/api/v1/openapi/transcriptions \{'\n'}
              {'  '}-H <span className="text-[#30d158]">"X-API-Key: $API_KEY"</span> \{'\n'}
              {'  '}-d <span className="text-[#ffb340]">'{'{'}"file_id":"$FILE_ID","language":"en"{'}'}'</span>
            </pre>
          </div>
          <SectionText num="06" tint="var(--tint-api)" title={t('home.landing.s6Title')}>
            <p>{t('home.landing.s6Body')}</p>
            <p>
              <Link to="/api" className="inline-flex items-center gap-1 font-medium text-accent hover:underline underline-offset-4">
                {t('home.landing.s6Cta')}
                <ChevronRight className="h-4 w-4 rtl:rotate-180" />
              </Link>
            </p>
          </SectionText>
        </section>
      </div>

      {/* How it works */}
      <section className="bg-card">
        <div className="shell py-24 sm:py-28">
          <div className="reveal text-center">
            <div className="num" style={{ ['--section-tint' as string]: 'var(--accent)' }}>07</div>
            <h2 className="h-section mt-5">{t('home.howItWorks.title')}</h2>
            <p className="t-lead mx-auto mt-5 max-w-[34rem]">{t('home.howItWorks.subtitle')}</p>
          </div>
          <ol className="reveal mt-14 grid gap-3 md:grid-cols-3">
            {howItWorksSteps.map((step, index) => (
              <li key={step.title} className="rounded-[22px] bg-raised p-7">
                <span className="figure text-[40px] text-accent">{index + 1}</span>
                <h3 className="mt-5 text-[19px] font-semibold tracking-[-0.015em] text-ink">{step.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-ink-2">{step.description}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Use cases */}
      <section id="testimonials" className="shell scroll-mt-20 py-24 sm:py-32">
        <div className="reveal text-center">
          <h2 className="h-section mx-auto max-w-[20ch] text-balance">{t('home.testimonials.title')}</h2>
          <p className="t-lead mx-auto mt-5 max-w-[36rem]">{t('home.testimonials.subtitle')}</p>
        </div>
        <div className="reveal mt-14 grid gap-x-12 gap-y-10 md:grid-cols-3">
          {useCases.map((item) => (
            <figure key={item.name} className="flex flex-col">
              <span className="h-6 font-serif text-[44px] leading-none text-ink-3" aria-hidden="true">“</span>
              <blockquote className="mt-3 text-pretty text-[16px] leading-[1.6] text-ink">{item.text}</blockquote>
              <figcaption className="mt-auto pt-5 text-[13px]">
                <span className="block font-semibold text-ink">{item.name}</span>
                <span className="mt-0.5 block text-ink-2">{item.role}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="shell scroll-mt-20 py-24 sm:py-32">
        <div className="grid gap-12 lg:grid-cols-[4fr_7fr]">
          <div className="reveal">
            <h2 className="h-section text-balance">{t('home.faq.title')}</h2>
            <p className="t-body mt-5">{t('home.faq.subtitle')}</p>
          </div>
          <div className="reveal divide-y divide-line border-y border-line">
            {faqItems.map((faq) => (
              <details key={faq.question} className="group py-1">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-6 py-4 text-start text-[17px] font-medium text-ink [&::-webkit-details-marker]:hidden">
                  {faq.question}
                  <Plus className="h-5 w-5 shrink-0 text-ink-3 transition-transform duration-300 group-open:rotate-45" />
                </summary>
                <p className="pb-5 pe-10 text-[15.5px] leading-relaxed text-ink-2">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* SEO: converters & formats */}
      <section className="shell pb-24">
        <div className="reveal panel grid gap-10 p-8 sm:p-10 lg:grid-cols-[1.2fr_1fr]">
          <div>
            <h2 className="text-[22px] font-semibold tracking-[-0.02em] text-ink">What is OmniScribe?</h2>
            <p className="mt-3 text-[15px] leading-relaxed text-ink-2">
              OmniScribe is an AI transcription workspace that converts audio, video, meetings, lectures, interviews, podcasts, and pasted media links into editable text. It supports 98+ languages, speaker labels, AI summaries, mind maps, key points, and export formats for captions, documents, and research workflows.
            </p>
            <h3 className="mt-8 text-[15px] font-semibold text-ink">More audio and video to text converters</h3>
            <p className="mt-2 text-[14px] leading-relaxed text-ink-2">
              OmniScribe handles common transcription jobs from speech, voice notes, podcasts, meetings, interviews, lectures, videos, and pasted media links in one browser-based workflow.
            </p>
            <Link to="/transcribe-audio-to-text" className="mt-3 inline-flex items-center gap-1 text-[14px] font-medium text-accent hover:underline underline-offset-4">
              Transcribe audio to text online
              <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
          </div>
          <div>
            <h3 className="text-[13px] font-semibold text-ink-2">Use cases</h3>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {transcriptionUseCases.map((useCase) => (
                <span key={useCase} className="rounded-full bg-raised px-3 py-1.5 text-[13px] font-medium text-ink">{useCase}</span>
              ))}
            </div>
            <h3 className="mt-6 text-[13px] font-semibold text-ink-2">Supported formats include</h3>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {supportedFormats.map((format) => (
                <span key={format} className="chip">{format}</span>
              ))}
            </div>
            <p className="mt-4 text-[13px] text-ink-3">Browser-side conversion tools can prepare incompatible media before transcription.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="shell pb-28 text-center">
        <div className="reveal">
          <h2 className="h-section mx-auto max-w-[18ch] text-balance">{t('home.cta.title')}</h2>
          <p className="t-lead mx-auto mt-5 max-w-[34rem]">{t('home.cta.subtitle')}</p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to={isAuthenticated ? '/upload' : '/register'} className="btn">
              {isAuthenticated ? t('dashboard.newTranscription') : t('auth.getStartedFree')}
              <ArrowRight className="h-4 w-4 rtl:rotate-180" />
            </Link>
            <Link to="/pricing" className="btn btn-quiet">{t('home.cta.viewPricing')}</Link>
          </div>
          <p className="t-small mt-5">{t('home.cta.noCreditCard')}</p>
        </div>
      </section>
    </div>
  );
};

export default Home;
